import { Collapse, Divider, IconButton, makeStyles } from '@material-ui/core';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import Icon from '@mdi/react';
import Loading from 'components/Loading/Loading';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getActivityDescription } from 'scenes/Project/components/ProjectOverview/activityFeedFormat';
import { selectAssignments } from 'store/assignments/selectors/assignment.selectors';
import { getProjectById } from 'store/projects/services/getProjectById.service';
import { selectProjects } from 'store/projects/selectors/projectsSelectors';
import { getRecordById } from 'store/records/services/getRecordById.service';
import { selectUserActiveEmployerInfo } from 'store/user/selectors.js/user.selectors';
import formatDurationBetween from 'utils/formatDurationBetween';
import ActivityLogImageThumb from './ActivityLogImageThumb';
import { getActivityLogIcons } from './activityLogIcons';

const useStyles = makeStyles((theme) => ({
  logRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
  logIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  logTime: {
    color: theme.palette.text.secondary,
    minWidth: 48,
  },
  logAction: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowWrap: 'anywhere',
  },
  logProject: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  details: {
    padding: theme.spacing(0.5, 1, 1.5, 7),
    fontSize: '0.85rem',
  },
  detailRow: {
    padding: theme.spacing(0.25, 0),
    color: theme.palette.text.secondary,
  },
  sectionTitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
  },
  changeRow: {
    padding: theme.spacing(0.25, 0),
  },
  changeOld: {
    color: theme.palette.error.main,
  },
  changeNew: {
    color: theme.palette.success.main,
  },
}));

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// Targets whose details the row can fetch by id when it is expanded.
const FETCHED_TARGET_TYPES = ['PROJECT', 'RECORD'];

export const IMAGE_TARGET_TYPE = 'FILEPERMISSION';

/** "12 Maalaus": an assignment's number and name, whichever it has. */
const formatAssignment = (assignment) =>
  [assignment?.number, assignment?.name].filter(Boolean).join(' ') || null;

/**
 * One activity-log row: the verb and target icons, the time (with how long
 * it lasted, when it was ended), and what the user did as the dashboard feed
 * words it ("Katsoi kuvaa · IMG_0042.jpg"). A row about an image shows the
 * image small (`image`, the API's verdict on it, see ActivityLogImageThumb);
 * clicking it calls `onOpenImage(item)`. A chevron expands a row whose
 * target can be fetched by id (project or record) or that has
 * metadata.changes (e.g. EDIT_PROJECT / EDIT_RECORD).
 */
const ActivityLogRow = ({ item, showProject = false, image, onOpenImage }) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const activeEmployerInfo = useSelector(selectUserActiveEmployerInfo);
  const projectEntities = useSelector(selectProjects);
  const assignmentEntities = useSelector(selectAssignments);

  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [fetched, setFetched] = useState(false);

  const { verbIcon, targetIcon } = getActivityLogIcons(item.action);
  const changes = item.metadata && item.metadata.changes;
  const isImage = item.targetType === IMAGE_TARGET_TYPE;
  const fetchesTarget =
    Boolean(item.targetId) && FETCHED_TARGET_TYPES.includes(item.targetType);
  const expandable = fetchesTarget || (changes && changes.length > 0);

  // An image is named as it is now where the viewer may see it, else as it
  // was named when it was viewed.
  const imageName =
    (image?.status === 'AVAILABLE' && image.name) || item.targetName || '';
  const description = getActivityDescription(
    t,
    isImage ? { ...item, targetName: imageName } : item
  );

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);

    if (!next || fetched || !fetchesTarget) return;

    setLoadingDetail(true);
    try {
      const data =
        item.targetType === 'PROJECT'
          ? await getProjectById(item.targetId, activeEmployerInfo)
          : await getRecordById(item.targetId, activeEmployerInfo);
      setDetail(data || null);
    } catch (e) {
      setDetail(null);
    } finally {
      setLoadingDetail(false);
      setFetched(true);
    }
  };

  const renderDetail = () => {
    if (loadingDetail) return <Loading tiny />;

    if (!detail) {
      return fetched && fetchesTarget ? (
        <div className={classes.detailRow}>
          {t('activity_log.detail_unavailable', 'Tietoja ei voitu hakea')}
        </div>
      ) : null;
    }

    if (item.targetType === 'PROJECT') {
      return (
        <>
          <div className={classes.detailRow}>
            {t('activity_log.detail_name', 'Nimi')}: {detail.name || '—'}
          </div>
          <div className={classes.detailRow}>
            {t('activity_log.detail_project_key', 'Projektiavain')}:{' '}
            {detail.projectKey || '—'}
          </div>
        </>
      );
    }

    if (item.targetType === 'RECORD') {
      // Names only: an id the store cannot name reads as unknown.
      const recordProjectName =
        (detail.project && projectEntities[detail.project]?.name) ||
        (detail.project === item.projectId && item.projectName) ||
        '—';
      const assignmentName =
        (detail.assignment &&
          formatAssignment(assignmentEntities?.[detail.assignment])) ||
        '—';

      return (
        <>
          <div className={classes.detailRow}>
            {t('activity_log.detail_clock_in', 'Sisäänleimaus')}:{' '}
            {detail.clockInAt
              ? moment(detail.clockInAt).format('l HH:mm')
              : '—'}
          </div>
          <div className={classes.detailRow}>
            {t('activity_log.detail_clock_out', 'Ulosleimaus')}:{' '}
            {detail.clockOutAt
              ? moment(detail.clockOutAt).format('l HH:mm')
              : '—'}
          </div>
          <div className={classes.detailRow}>
            {t('activity_log.detail_project', 'Projekti')}: {recordProjectName}
          </div>
          <div className={classes.detailRow}>
            {t('activity_log.detail_assignment', 'Littera')}: {assignmentName}
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div>
      <div className={classes.logRow}>
        <span className={classes.logIcons}>
          {verbIcon}
          {targetIcon}
        </span>
        <span className={classes.logTime}>
          {moment(item.createdAt).format('HH:mm')}
          {item.endedAt
            ? ` · ${formatDurationBetween(item.createdAt, item.endedAt)}`
            : ''}
        </span>
        {isImage ? (
          <ActivityLogImageThumb
            filePermissionId={item.targetId}
            image={image}
            name={imageName}
            onOpen={onOpenImage ? () => onOpenImage(item) : undefined}
          />
        ) : null}
        <span className={classes.logAction}>
          <span data-activity-log-description>{description}</span>
          {showProject && item.projectName ? (
            <span className={classes.logProject}>{item.projectName}</span>
          ) : null}
        </span>
        {expandable ? (
          <IconButton
            size="small"
            onClick={handleToggle}
            aria-expanded={open}
            aria-label={
              open
                ? t('activity_log.hide_details', 'Piilota tiedot')
                : t('activity_log.show_details', 'Näytä tiedot')
            }
          >
            <Icon path={open ? mdiChevronUp : mdiChevronDown} size={1} />
          </IconButton>
        ) : (
          <span style={{ width: 30, flexShrink: 0 }} aria-hidden="true" />
        )}
      </div>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <div className={classes.details}>
          {renderDetail()}

          {changes && changes.length > 0 ? (
            <>
              <div className={classes.sectionTitle}>
                {t('activity_log.changes', 'Muutokset')}
              </div>
              {changes.map((change, index) => (
                <div
                  className={classes.changeRow}
                  key={`${change.field}-${index}`}
                >
                  {change.field}:{' '}
                  <span className={classes.changeOld}>
                    {formatValue(change.old)}
                  </span>{' '}
                  →{' '}
                  <span className={classes.changeNew}>
                    {formatValue(change.new)}
                  </span>
                </div>
              ))}
            </>
          ) : null}
        </div>
      </Collapse>

      <Divider />
    </div>
  );
};

export default ActivityLogRow;
